/** NONLIVE graph qualification. No game registration, inferred geography,
 * costs, ranges, guest demand or save migration. Caller supplies every link. */
export interface AccessNode { id: string }
export interface AccessLink { id: string; from: string; to: string; distance: number; blocked: boolean }
export interface AccessGraph { nodes: readonly AccessNode[]; links: readonly AccessLink[] }
export function validateAccessGraph(graph: AccessGraph): string[] {
  const errors: string[] = [], nodes = new Set<string>(), links = new Set<string>();
  for (const n of graph.nodes) {
    if (n.id === '' || nodes.has(n.id)) errors.push(`Invalid or duplicate node ${n.id}`);
    nodes.add(n.id);
  }
  for (const l of graph.links) {
    if (l.id === '' || links.has(l.id)) errors.push(`Invalid or duplicate link ${l.id}`);
    links.add(l.id);
    if (!nodes.has(l.from) || !nodes.has(l.to)) errors.push(`Unknown endpoint on ${l.id}`);
    if (!Number.isFinite(l.distance) || l.distance < 0) errors.push(`Invalid distance on ${l.id}`);
  }
  return errors;
}
/** Undirected distances over explicit unblocked links. No radius policy. */
export function walkingDistances(graph: AccessGraph, entrance: string): Record<string, number> {
  const errors = validateAccessGraph(graph);
  if (errors.length > 0) throw new Error(errors.join('; '));
  if (!graph.nodes.some(n => n.id === entrance)) throw new Error('Unknown entrance');
  const distances: Record<string, number> = { [entrance]: 0 };
  const pending = new Set(graph.nodes.map(n => n.id));
  while (pending.size > 0) {
    let closest: string | null = null;
    for (const id of [...pending].sort()) if (distances[id] !== undefined && (closest === null || distances[id] < distances[closest])) closest = id;
    if (closest === null) break;
    pending.delete(closest);
    for (const link of graph.links) {
      if (link.blocked) continue;
      const next = link.from === closest ? link.to : link.to === closest ? link.from : null;
      if (next === null || !pending.has(next)) continue;
      const d = distances[closest] + link.distance;
      if (distances[next] === undefined || d < distances[next]) distances[next] = d;
    }
  }
  return distances;
}
